import React from 'react'
import { getMentionPattern, getAgentByMention, getBackendAgentPattern } from '@/constants/agentNames'

/**
 * Color class mapping for agent mentions.
 * Uses canonical agent IDs as keys.
 */
const AGENT_COLOR_CLASSES: Record<string, string> = {
  amber: 'text-amber-600 font-medium',
  blue: 'text-blue-600 font-medium',
  green: 'text-green-600 font-medium',
  purple: 'text-purple-600 font-medium',
}

// Default to amber (plant support) if agent not found
const DEFAULT_COLOR_CLASS = AGENT_COLOR_CLASSES.amber

// Memoize patterns outside function for performance (avoids recreation on every render)
const MENTION_PATTERN = getMentionPattern()
const BACKEND_AGENT_PATTERN = getBackendAgentPattern()

/**
 * Highlights @agent mentions in text with appropriate colors.
 * IMPORTANT: Only shows mentions for synthetic AI engineers (AICE, MAX, ELLA, PAM).
 * Backend agent mentions like @pid_graph_agent are hidden from users.
 *
 * @ai-plant-support-engineer (or legacy @pam) -> amber
 * @ai-chemical-engineer (or legacy @aice) -> blue
 * @ai-mechanical-engineer (or legacy @max) -> green
 * @ai-electrical-engineer (or legacy @ella) -> purple
 */
export const highlightAgentMentions = (text: string): React.ReactNode => {
  // First, strip out any backend agent mentions that aren't in our synthetic agent list
  // This removes mentions like @pid_graph_agent, @sql_agent, etc. from the display
  // Uses centralized pattern from constants for maintainability
  const cleanedText = text
    .replace(BACKEND_AGENT_PATTERN, '') // Remove backend agent mentions
    .replace(/\s{2,}/g, ' ') // Collapse multiple spaces into single space
    .trim() // Remove leading/trailing whitespace

  // Use centralized mention pattern from constants (only synthetic agents)
  const mentionPattern = MENTION_PATTERN

  // Split text by mentions while keeping the mentions
  const parts = cleanedText.split(mentionPattern)

  return (
    <>
      {parts.map((part, index) => {
        // Check if this part is a mention
        if (part.startsWith('@')) {
          const agentAlias = getAgentByMention(part)
          if (agentAlias) {
            const colorClass = AGENT_COLOR_CLASSES[agentAlias.color] || DEFAULT_COLOR_CLASS
            return (
              <span key={index} className={colorClass}>
                {part}
              </span>
            )
          }
        }

        // Regular text
        return <React.Fragment key={index}>{part}</React.Fragment>
      })}
    </>
  )
}
